import {
  initialize,
  LDClient,
  LDContext,
  LDOptions
} from 'launchdarkly-js-client-sdk'
import { Logger } from './helperMethods'

interface LaunchDarklyClientParams {
  clientId: string
  context: LDContext
  options?: LDOptions
}

/**
 * Service class for interacting with the LaunchDarkly client.
 */
export class LaunchDarklyService {
  private static instance: LDClient | null = null
  private static initializationState: Promise<void> | null = null
  private static initializationComplete = false

  // Prevent instantiation
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  public static initializeClient(params: LaunchDarklyClientParams): void {
    if (!this.instance) {
      this.instance = initialize(
        params.clientId,
        params.context,
        params.options || {}
      )

      this.initializationState = this.instance
        .waitForInitialization(5)
        .then(() => {
          this.initializationComplete = true
          Logger.log('LaunchDarkly client initialized successfully')
        })
        .catch((error) => {
          Logger.strError(
            'LaunchDarkly client failed to initialize, using default values',
            error
          )
        })
    }
  }

  public static async getInstance(): Promise<LDClient> {
    if (!this.instance) {
      Logger.strError(
        'LaunchDarkly client not initialized. Call initializeClient() first.'
      )
      return
    }

    if (!this.initializationComplete) {
      await this.initializationState
    }

    return this.instance
  }

  public static async getFlagValue<T>(
    flagKey: string,
    defaultValue: T
  ): Promise<T> {
    const client = await this.getInstance()
    if (!client) {
      return defaultValue
    }
    return client.variation(flagKey, defaultValue)
  }
}
